import { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import {
  PaintBlockSelectionPill,
  PaintBlockSelectionPillCounter,
  StyledToggleButtonGroup,
} from './styles';
import { useAtom, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import * as localState from '../../state';
import { usePaintRegionGroupsQuery } from '@/__generated__/graphql';
import { useMemo } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Skeleton } from '@mui/material';

type Props = {
  compact?: boolean;
  onSavedBlocksClick?: () => void;
} & ToggleButtonGroupProps;

const SAVED_BLOCKS_VALUE = 'saved-blocks';

export const PaintBlockTopFilter = ({ sx, compact = false, ...toggleGroupProps }: Props) => {
  const structureId = useGetStructureId();

  const [selectedPaintRegionGroup, setelectedPaintRegionGroup] = useAtom(
    state.selectedPaintRegionGroup
  );
  const setCustomBlocksQueryInput = useSetAtom(localState.customBlocksQueryInput);

  const [isSavedBlocksSelected, setIsSavedBlocksSelected] = useAtom(
    state.isSavedBlocksSelected
  );

  const { data, loading } = usePaintRegionGroupsQuery({
    variables: {
      input: { structureId },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    onCompleted: (response) => {
      // Initializing the filter to the default filter
      // which is 'Paint Regions'
      const defaultPaintGroupId = response?.paintRegionGroups?.find((item) => item.isDefault);
      if (defaultPaintGroupId) {
        setelectedPaintRegionGroup((previous) => {
          // If there's already a filter set, don't interfere
          return previous || defaultPaintGroupId;
        });
      }
    },
  });

  const options = useMemo(() => {
    const paintRegionOptions = (data?.paintRegionGroups || []).map((group) => ({
      value: group?.id,
      label: group?.name,
      count: 0,
      type: 'paint-region' as const,
    }));

    // Only add Saved Blocks option if there are paint region options
    if (paintRegionOptions.length > 0) {
      const savedBlocksOption = {
        value: SAVED_BLOCKS_VALUE,
        label: compact ? 'Saved' : 'Saved Blocks',
        count: 0,
        type: 'saved-blocks' as const,
      };
      return [...paintRegionOptions, savedBlocksOption];
    }

    return paintRegionOptions;
  }, [data?.paintRegionGroups, compact]);

  if (loading)
    return (
      <Skeleton variant="rectangular" width={'70%'} height={30} sx={{ borderRadius: 2, mt: 1 }} />
    );

  if (!options?.length) return <></>;

  // Determine the current selected value
  const currentValue = isSavedBlocksSelected
    ? SAVED_BLOCKS_VALUE
    : selectedPaintRegionGroup?.id || SAVED_BLOCKS_VALUE;

  return (
    <StyledToggleButtonGroup
      value={currentValue}
      exclusive
      sx={sx}
      onChange={(_, value: string) => {
        if (!value) return;

        // Handle Saved Blocks selection
        if (value === SAVED_BLOCKS_VALUE) {
          setelectedPaintRegionGroup(null);
          setIsSavedBlocksSelected(true);
          return;
        }

        // Handle paint region group selection
        setIsSavedBlocksSelected(false);
        if (value === selectedPaintRegionGroup?.id) return;

        const foundGroup = data?.paintRegionGroups?.find((group) => group.id === value);
        setelectedPaintRegionGroup(foundGroup);
        // Resetting this state, to let the CustomBlockList know that
        // there are more items to fetch
        setCustomBlocksQueryInput((prev) => ({
          ...prev,
          hasMoreItems: true,
          page: 2,
        }));
      }}
      {...toggleGroupProps}
    >
      {options.map(({ count, label, value }) => (
        <PaintBlockSelectionPill key={value} value={value}>
          {compact ? label.replace('Blocks', '') : label}
          {!compact && <PaintBlockSelectionPillCounter>{count}</PaintBlockSelectionPillCounter>}
        </PaintBlockSelectionPill>
      ))}
    </StyledToggleButtonGroup>
  );
};
